import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { CheckCircle, Loader } from 'lucide-react';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import './CheckoutSuccess.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccess = () => {
    // Set page title
  useEffect(() => {
    document.title = "Payment Successful - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [authReady, setAuthReady] = useState(false);
  const sessionId = searchParams.get('session_id');

  // Wait for Firebase auth to be ready
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('✅ CheckoutSuccess: Firebase auth ready, user:', user.email);
        setAuthReady(true);
      } else {
        console.warn('⚠️ CheckoutSuccess: No user logged in, redirecting to login');
        toast.error('Please log in to verify your payment');
        navigate('/auth');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!sessionId) {
      toast.error('Invalid checkout session');
      navigate('/dashboard');
      return;
    }

    // Only start polling when auth is ready
    if (authReady) {
      pollPaymentStatus();
    }
  }, [sessionId, authReady]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      toast.error('Payment verification timed out. Please check your account.');
      setTimeout(() => navigate('/dashboard'), 3000);
      return;
    }

    try {
      // Get fresh token before making the request
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }
      const idToken = await user.getIdToken(true);
      
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      const data = response.data;

      console.log('✅ CheckoutSuccess: Payment status response:', data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentInfo(data);
        toast.success(data.message);
        setTimeout(() => navigate('/dashboard'), 3000);
      } else if (data.status === 'expired') {
        setStatus('expired');
        toast.error('Payment session expired');
        setTimeout(() => navigate('/dashboard'), 3000);
      } else {
        // Still pending, continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      toast.error('Error verifying payment. Please contact support.');
      setTimeout(() => navigate('/dashboard'), 3000);
    }
  };

  return (
    <div className="checkout-success-container">
      <div className="checkout-success-content">
        {status === 'checking' && (
          <div className="status-checking">
            <Loader className="spinner" size={64} />
            <h2>Verifying your payment...</h2>
            <p>Please wait while we confirm your payment with Stripe.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="status-success">
            <CheckCircle size={64} className="success-icon" />
            <h2>Payment Successful!</h2>
            <p>{paymentInfo?.message}</p>
            {paymentInfo?.credits_added && (
              <div className="credits-info">
                <p><strong>{paymentInfo.credits_added} credits</strong> have been added to your account.</p>
                <p>Plan: <strong>{paymentInfo.plan}</strong></p>
              </div>
            )}
            <p className="redirect-message">Redirecting you to the dashboard...</p>
          </div>
        )}

        {status === 'timeout' && (
          <div className="status-error">
            <h2>Verification Timeout</h2>
            <p>We're still processing your payment. Please check your dashboard shortly.</p>
          </div>
        )}

        {status === 'expired' && (
          <div className="status-error">
            <h2>Session Expired</h2>
            <p>Your payment session has expired. Please try again.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="status-error">
            <h2>Verification Error</h2>
            <p>We encountered an error verifying your payment. Please contact support if the issue persists.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
